import { Navigate, createFileRoute } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { PublicLayout } from '@/components/public/PublicLayout';
import { ConfirmLandingCard } from '@/components/public/confirm/ConfirmLandingCard';
import { ConfirmSuccessState } from '@/components/public/confirm/ConfirmSuccessState';
import { TokenExpiredState } from '@/components/public/confirm/TokenExpiredState';
import { useConfirmLanding } from '@/hooks/public/useConfirmLanding';

/**
 * Public landing page for the Click-to-Confirm short link.
 *
 * Backend mounts the underlying API at `/api/public/confirm/:token`; the
 * marketing short link is `ntfi.co/c/:token`, which is expected to
 * redirect to `app.notifi.com/c/:token` (this route). See
 * `apps/operations/src/modules/reviews/click-to-confirm/confirm.controller.ts`.
 *
 * Idempotency: if the customer re-opens the link after acting, the
 * `landing` payload returns the prior `status` (CONFIRMED,
 * RESCHEDULE_REQUESTED, CANCELLED).
 *
 * - `PENDING` → the full landing (Confirm / Reschedule / Cancel).
 * - `CONFIRMED` with a still-valid token → the landing WITHOUT the Confirm CTA,
 *   so an already-confirmed appointment (e.g. an online booking that lands
 *   confirmed) can still be rescheduled or cancelled instead of dead-ending.
 * - `CANCELLED` / `RESCHEDULE_REQUESTED` → the terminal outcome (no CTAs); a
 *   used/revoked/expired token is caught above by the `isStale` guard.
 */
export const Route = createFileRoute('/(public)/c/$token')({
  component: ConfirmLandingPage,
});

function ConfirmLandingPage() {
  const { token } = Route.useParams();
  const { data, isLoading, isError } = useConfirmLanding(token);

  if (isLoading) {
    return (
      <PublicLayout>
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </PublicLayout>
    );
  }

  if (isError || !data) {
    return <TokenExpiredState />;
  }

  // Terminal outcomes render the prior result regardless of token staleness —
  // so a customer who just cancelled / requested a reschedule (which spends the
  // token) sees that outcome, not an "expired" dead-end.
  if (data.status === 'CANCELLED') {
    return (
      <ConfirmSuccessState
        status={data.status}
        tenantName={data.branding.tenantName}
        logoUrl={data.branding.logoUrl}
      />
    );
  }

  // Confirmation tokens also authenticate the durable booking manager. Once
  // the customer has acted, keep the same link useful by showing the complete
  // schedule, assigned team, policy, and available management actions.
  if (
    data.managementAvailable &&
    (data.status === 'CONFIRMED' || data.status === 'RESCHEDULE_REQUESTED')
  ) {
    return <Navigate to="/book/$token" params={{ token }} replace />;
  }

  // Confirmed appointment: while the token is still valid, keep it changeable —
  // show the landing with reschedule / cancel (no Confirm CTA) instead of a
  // dead-end. Once the token is spent, fall back to the confirmed outcome.
  if (data.status === 'CONFIRMED') {
    return data.isStale ? (
      <ConfirmSuccessState
        status={data.status}
        tenantName={data.branding.tenantName}
        logoUrl={data.branding.logoUrl}
      />
    ) : (
      <ConfirmLandingCard token={token} payload={data} alreadyConfirmed />
    );
  }

  // PENDING: a stale (used/revoked/expired) link with nothing done is a dead
  // link; a live one shows the full Confirm / Reschedule / Cancel landing.
  if (data.isStale) {
    return <TokenExpiredState message="This confirmation link has expired." />;
  }

  return <ConfirmLandingCard token={token} payload={data} />;
}
